import type { Pets } from "@highmarch/app";
import type { PetData } from "@highmarch/domain";
import type { FeatureRegistry, HubApi } from "../../features/registry";
import { toast } from "../toast";
import { petChipHtml } from "./petChip";
import { openPetPick } from "./petPick";
import { equipToast, treatToast } from "./petWords";
import { openTreatPick, treatWellHtml } from "./treatWell";

/** The pet shown on a surface: the one coming along, else (going without) the first had, dimmed. */
export function shownPet(pets: Pets): { pet: PetData; on: boolean } | null {
  const eq = pets.equipped();
  if (eq) return { pet: eq, on: true };
  const first = pets.owned()[0];
  return first ? { pet: first, on: false } : null;
}

export interface PetRowDeps {
  readonly root: HTMLElement;
  readonly pets: Pets;
  readonly registry: FeatureRegistry;
  readonly hub: () => HubApi | null;
  /** The coat chance with no treat (%). */
  readonly base: number;
  readonly persist: () => void;
  /** The pet coming along changed: the renderer wears it again. */
  readonly changed: () => void;
}

/**
 * The pre-battle pet row's taps (Pets v2/v3), delegated on the UI root (the hub and the map redraw their markup): the
 * chip opens the picker of every pet had (petPick.ts: which comes along, or none), the choice redrawn in place; the
 * treat well opens its picker (a biscuit armed or put away), or with none held goes to the Shop's Pet Treats (the
 * battle card) or says where they are sold (the map panel).
 */
export function bindPetRowTaps(d: PetRowDeps): void {
  const { root, pets } = d;
  if (typeof root.addEventListener !== "function") return;   // a headless registry (tests) has no UI root to listen on
  const shop = (): void => {
    const h = d.hub();
    if (!h) { toast(root, "Get Pet Treats in the Shop"); return; }
    d.registry.openShop(h, "gems");
    // the Gems page draws its goods first: the Pet Treats shelf is there on the next frame
    requestAnimationFrame(() => h.parent.querySelector(".sheet.shop .ssec.treats")?.scrollIntoView({ block: "start", behavior: "smooth" }));
  };
  const redrawTreats = (): void => { const html = treatWellHtml(pets.treats(), d.base); for (const b of root.querySelectorAll<HTMLElement>(".prebattle .pettreat")) b.outerHTML = html; };
  const choose = (id: string | null): void => {
    if (id === (pets.equipped()?.id ?? null) || !pets.equip(id)) return;
    d.persist();
    d.changed();
    const s = shownPet(pets);
    if (!s) return;
    const html = petChipHtml(pets.card(s.pet), s.on);
    for (const b of root.querySelectorAll<HTMLElement>(".prebattle .petchip")) b.outerHTML = html;
    toast(root, equipToast(s.pet, s.on ? pets.card(s.pet) : null));
  };
  root.addEventListener("click", (e) => {
    const t = e.target as HTMLElement | null;
    const well = t?.closest?.<HTMLElement>(".prebattle .pettreat");
    if (well) {
      const slots = pets.treats(), onCard = well.closest(".pb-card") !== null;
      if (!slots.some((s) => s.have > 0)) { if (onCard) shop(); else toast(root, "Get Pet Treats in the Shop"); return; }
      openTreatPick(well.closest<HTMLElement>(".hub, .campaign") ?? root, well, slots, onCard && d.hub() !== null, {
        pick: (id) => {
          const r = pets.toggleTreat(id);
          if (r !== "armed" && r !== "disarmed") return;
          d.persist();
          redrawTreats();
          toast(root, treatToast(r === "armed" ? pets.treats().find((s) => s.armed) ?? null : null, d.base));
        },
        shop,
      });
      return;
    }
    const chip = t?.closest?.<HTMLElement>(".prebattle .petchip:not(.nopet)");
    if (!chip) return;
    openPetPick(chip.closest<HTMLElement>(".hub, .campaign") ?? root, chip, pets.owned().map((p) => pets.card(p)), pets.equipped()?.id ?? null, choose);
  });
}
